// /terms and /privacy — accessible logged in or out (see App.tsx, which
// renders this before any auth gating). One component for both since the
// layout (narrow column, back link, dated sections) is identical; only the
// copy differs.
const EFFECTIVE_DATE = "July 26, 2026"

interface LegalPageProps {
	page: "terms" | "privacy"
}

export function LegalPage({ page }: LegalPageProps) {
	return (
		<div class="flex-grow-1 overflow-auto">
			<div class="container py-4">
				<div class="row justify-content-center">
					<div class="col-12 col-md-8 col-lg-6">
						<a
							href="/"
							class="d-inline-flex align-items-center gap-1 text-decoration-none text-body-secondary small mb-3"
						>
							<i class="bi bi-arrow-left" aria-hidden="true" />
							<span>Back to Artbox</span>
						</a>
						{page === "terms" ? <TermsContent /> : <PrivacyContent />}
					</div>
				</div>
			</div>
		</div>
	)
}

function TermsContent() {
	return (
		<article>
			<h1 class="h3">Terms of Service</h1>
			<p class="text-body-secondary small">Last updated: {EFFECTIVE_DATE}</p>

			<h2 class="h6 mt-4">1. Acceptance</h2>
			<p>
				By creating an account or using Artbox, you agree to these terms. If you
				don't agree, please don't use the service.
			</p>

			<h2 class="h6 mt-4">2. The service</h2>
			<p>
				Artbox is a small, private app for cataloging children's artwork: photos
				and optional audio/video recordings, organized by date inside per-kid
				"boxes." It's built for families and invited relatives, not the general
				public.
			</p>

			<h2 class="h6 mt-4">3. Accounts</h2>
			<p>
				Accounts are for adults. You're responsible for keeping your password
				secure and for all activity under your account. Use a real email address
				you control. It's how you sign in and how invites reach you.
			</p>

			<h2 class="h6 mt-4">4. Your content stays yours</h2>
			<p>
				You own everything you upload. We only store and display it back to you
				and the people you invite to a box. We don't claim any rights beyond
				what's needed to run the service for you.
			</p>

			<h2 class="h6 mt-4">5. Acceptable use</h2>
			<p>
				Don't upload content you don't have the right to share, don't use Artbox
				for anything illegal, and don't try to disrupt or abuse the service
				(scraping, attacking, overloading it, etc.).
			</p>

			<h2 class="h6 mt-4">6. Invited viewers</h2>
			<p>
				Box owners choose who to invite and what role they get: owners can add
				and edit art and manage members; viewers can only look. Owners are
				responsible for who they invite into a box.
			</p>

			<h2 class="h6 mt-4">7. No warranty</h2>
			<p>
				Artbox is provided "as is." We try hard to keep it running and your
				memories safe, but we can't guarantee the service will always be
				available or error-free.
			</p>

			<h2 class="h6 mt-4">8. Limitation of liability</h2>
			<p>
				To the extent allowed by law, Artbox isn't liable for indirect,
				incidental, or consequential damages arising from your use of the
				service.
			</p>

			<h2 class="h6 mt-4">9. Changes</h2>
			<p>
				We may update these terms occasionally. We'll update the date at the top
				when we do; continued use means you accept the changes.
			</p>

			<h2 class="h6 mt-4">10. Contact</h2>
			<p>
				Questions about these terms? Reach out to the person who runs your
				family's Artbox instance.
			</p>
		</article>
	)
}

function PrivacyContent() {
	return (
		<article>
			<h1 class="h3">Privacy Policy</h1>
			<p class="text-body-secondary small">Last updated: {EFFECTIVE_DATE}</p>

			<h2 class="h6 mt-4">What we store</h2>
			<p>
				Your email and name, your password (hashed with bcrypt; we never store
				or see it in plain text), the photos/audio/video you upload (hosted on
				Cloudinary), and the dates, titles, and notes you attach to each piece.
			</p>

			<h2 class="h6 mt-4">What we don't do</h2>
			<p>
				No ads. No tracking. We don't sell your data. No third-party analytics
				scripts. The only outside service involved is Cloudinary, which hosts
				your media files.
			</p>

			<h2 class="h6 mt-4">Who can see your content</h2>
			<p>
				Only the people you invite to a box. Media files are served from
				unguessable Cloudinary URLs: technically public links if someone gets
				ahold of one, but never listed or discoverable anywhere.
			</p>

			<h2 class="h6 mt-4">Cookies</h2>
			<p>
				A single session cookie, used only to keep you signed in. No advertising
				or tracking cookies.
			</p>

			<h2 class="h6 mt-4">Retention and deletion</h2>
			<p>
				Deleting a piece of art or a whole box removes its photos and recordings
				from Cloudinary permanently. To delete your account entirely, contact
				the person who runs your family's Artbox instance.
			</p>

			<h2 class="h6 mt-4">Children's privacy</h2>
			<p>
				Accounts are for adults only. Any content depicting or describing a
				child is uploaded by a parent or guardian on that child's behalf. Artbox
				doesn't knowingly collect information directly from children.
			</p>

			<h2 class="h6 mt-4">Changes</h2>
			<p>
				We may update this policy occasionally. We'll update the date at the top
				when we do.
			</p>

			<h2 class="h6 mt-4">Contact</h2>
			<p>
				Questions about your data? Reach out to the person who runs your
				family's Artbox instance.
			</p>
		</article>
	)
}
